import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { FaWhatsapp, FaSpinner, FaGift, FaShieldAlt } from "react-icons/fa";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import * as Tabs from "@radix-ui/react-tabs";
import { toast } from "react-hot-toast";
import "react-datepicker/dist/react-datepicker.css";
import CoachReviews from "./CoachReviews";
import CoachGalleryads from "./CoachGalleryads";
import { Helmet } from "react-helmet-async";
import { API_ORIGIN as API_BASE } from "../config/api";
import GoogleSignInButton from "./GoogleSignInButton";
import { signInWithGoogleCredential } from "../api/clientAuth";
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

function CoachDetail() {
  const params = useParams();
  const navigate = useNavigate();

  const raw = params.slug ?? params.id ?? "";

  const coachId = useMemo(() => {
    if (!raw) return null;
    const first = String(raw).split("-")[0];
    const n = Number(first);
    return Number.isNaN(n) ? null : n;
  }, [raw]);

  // ----- data -----
  const [coach, setCoach] = useState(null);
  

  // ----- client auth -----
  const [clientToken, setClientToken] = useState("");
  const [clientUser, setClientUser] = useState(null);
  const [showClientLoginModal, setShowClientLoginModal] = useState(false);
  const [pendingContactAction, setPendingContactAction] = useState(null);
  const [clientSigningIn, setClientSigningIn] = useState(false);
  const [clientSignInError, setClientSignInError] = useState("");

  // ----- booking form -----
  const [bookingDate, setBookingDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [studentName, setStudentName] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [phoneCode, setPhoneCode] = useState("+971");
  const [studentPhone, setStudentPhone] = useState("");
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errors, setErrors] = useState({});
  const [requestSent, setRequestSent] = useState(false);

  // ----- gifts -----
  const [giftNote, setGiftNote] = useState("");
  const [giftLoading, setGiftLoading] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);

  // =========================
  // Visitor session id for anonymous profile views
  // =========================
  const getOrCreateVisitorSessionId = () => {
    let sessionId = localStorage.getItem("visitorSessionId");
    if (!sessionId) {
      sessionId =
        "vs_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).slice(2, 10);
      localStorage.setItem("visitorSessionId", sessionId);
    }
    return sessionId;
  };

  // =========================
  // Load saved client auth
  // =========================
  useEffect(() => {
    const token = localStorage.getItem("clientToken") || "";
    const userRaw = localStorage.getItem("clientUser");

    setClientToken(token);

    if (userRaw) {
      try {
        setClientUser(JSON.parse(userRaw));
      } catch {
        setClientUser(null);
      }
    }
  }, []);

  const isLoggedIn = !!clientToken && !!clientUser;

  // =========================
  // Restore pending action after redirect
  // =========================
  useEffect(() => {
    const savedAction = sessionStorage.getItem("pendingContactAction");
    if (savedAction) {
      setPendingContactAction(savedAction);
    }
  }, []);

  useEffect(() => {
    const savedAction =
      pendingContactAction || sessionStorage.getItem("pendingContactAction");

    if (!savedAction) return;
    if (!isLoggedIn) return;
    if (!coach?.id) return;

    unlockContactAndProceed(savedAction, clientToken).finally(() => {
      setPendingContactAction(null);
      sessionStorage.removeItem("pendingContactAction");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, coach?.id, clientToken]);

  // =========================
  // Fetch coach by ID
  // =========================
  useEffect(() => {
    if (!coachId) return;

    axios
      .get(`${API_BASE}/api/Coaches/${coachId}`)
      .then((res) => setCoach(res.data))
      .catch(() => toast.error("Failed to fetch coach details."));
  }, [coachId]);

  // =========================
  // Track profile view
  // =========================
  useEffect(() => {
    if (!coach?.id) return;

    const sessionId = getOrCreateVisitorSessionId();
    const headers = {};

    if (clientToken) {
      headers.Authorization = `Bearer ${clientToken}`;
    }

    axios
      .post(
        `${API_BASE}/api/coach-profile-view/track/${coach.id}`,
        {
          sessionId,
          viewSource: "coach_page",
          clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        { headers }
      )
      .catch(() => {
        // silent fail
      });
  }, [coach?.id, clientToken]);

  // =========================
  // Date helpers
  // =========================
  const todayLocalMidnight = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const ymdFromDate = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${dd}`;
  };

  // =========================
  // Availability
  // =========================
  const availableDates = useMemo(() => {
    if (!coach?.availabilities?.length) return [];
    const set = new Set(
      coach.availabilities
        .map((a) => (a.availableDate || "").slice(0, 10))
        .filter(Boolean)
    );
    return [...set];
  }, [coach?.availabilities]);

  const availableDateSet = useMemo(
    () => new Set(availableDates),
    [availableDates]
  );

  const highlightedDates = useMemo(
    () => availableDates.map((d) => new Date(`${d}T00:00:00`)),
    [availableDates]
  );

  const confirmedBookedSet = useMemo(() => {
    const set = new Set();
    const list = coach?.bookings || [];
    for (const b of list) {
      const ymd = (b?.bookingDate || "").slice(0, 10);
      const status = String(b?.status || "").toLowerCase();
      if (
        ymd &&
        (status === "confirmed" || status === "1" || status === "approved")
      ) {
        set.add(`${ymd}__${b.timeSlot}`);
      }
    }
    return set;
  }, [coach?.bookings]);

  const getTimeSlotsForDate = (dateYmd) => {
    if (!coach?.availabilities) return [];

    let slots = coach.availabilities
      .filter((a) => (a.availableDate || "").startsWith(dateYmd))
      .map((a) => a.timeSlot)
      .filter(Boolean);

    slots = slots.filter(
      (slot) => !confirmedBookedSet.has(`${dateYmd}__${slot}`)
    );

    return [...new Set(slots)].sort((a, b) =>
      String(a).localeCompare(String(b))
    );
  };

  // =========================
  // Contact helpers
  // =========================
  const clearClientSession = () => {
    localStorage.removeItem("clientToken");
    localStorage.removeItem("clientUser");
    sessionStorage.removeItem("pendingContactAction");
    setClientToken("");
    setClientUser(null);
  };

  const handleGoogleCredential = async (response) => {
    setClientSigningIn(true);
    setClientSignInError("");
    try {
      const { token, client } = await signInWithGoogleCredential(response?.credential);
      localStorage.setItem("clientToken", token);
      localStorage.setItem("clientUser", JSON.stringify(client));
      setClientToken(token);
      setClientUser(client);
      setShowClientLoginModal(false);
    } catch {
      setClientSignInError("Sign-in failed. Please try again.");
    } finally {
      setClientSigningIn(false);
    }
  };

  const requireClientLogin = (actionType) => {
    setPendingContactAction(actionType);
    sessionStorage.setItem("pendingContactAction", actionType);
    setShowClientLoginModal(true);
  };

const unlockContactAndProceed = async (actionType, overrideToken = null) => {
  try {
    const tokenToUse = overrideToken || clientToken;

    if (!tokenToUse) {
      requireClientLogin(actionType);
      return;
    }

    const res = await axios.post(
      `${API_BASE}/api/client-contact/unlock/${coach.id}`,
      {
        actionType,
        clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      {
        headers: {
          Authorization: `Bearer ${tokenToUse}`,
        },
      }
    );

    const { whatsappPhone, email, phone } = res.data || {};

if (actionType === "click_whatsapp") {
  const number = whatsappPhone || phone || coach?.phoneNumber;
  if (!number) {
    toast.error("WhatsApp number not available.");
    return;
  }

  const cleanedNumber = String(number).replace(/[^\d]/g, "");

  const msg = [
    `Hello ${coach.fullName}, I'm interested in your sessions.`,
    "",
    coachUrl,
  ].join("\n");

  window.open(
    `https://wa.me/${cleanedNumber}?text=${encodeURIComponent(msg)}`,
    "_blank"
  );
  return;
}
    if (actionType === "click_email") {
      const targetEmail = email || coach?.email;
      if (!targetEmail) {
        toast.error("Email not available.");
        return;
      }

      window.location.href = `mailto:${targetEmail}`;
      return;
    }

    if (actionType === "click_phone") {
      const targetPhone = phone || coach?.phoneNumber;
      if (!targetPhone) {
        toast.error("Phone not available.");
        return;
      }

      window.location.href = `tel:${targetPhone}`;
    }
  } catch (err) {
    if (err?.response?.status === 401) {
      clearClientSession();
      toast.error("Please sign in again.");
      requireClientLogin(actionType);
      return;
    }

    toast.error(err?.response?.data?.message || "Unable to unlock contact.");
  }
};
  // =========================
  // Booking handlers
  // =========================
  const validateForm = () => {
    const newErrors = {};
    if (!studentName.trim()) newErrors.studentName = "Name is required";
    if (!studentEmail.trim()) {
      newErrors.studentEmail = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(studentEmail)) {
      newErrors.studentEmail = "Email is invalid";
    }
    if (!studentPhone.trim()) newErrors.studentPhone = "Phone is required";
    if (!bookingDate) newErrors.bookingDate = "Please select a date";
    if (!timeSlot) newErrors.timeSlot = "Please select a time slot";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRequest = () => {
    if (!validateForm()) {
      toast.error("Please correct the errors above.");
      return;
    }
    setShowConfirmation(true);
  };

  const confirmRequest = async () => {
    const startTime = timeSlot.split(" - ")[0];
    const iso = new Date(`${bookingDate}T${startTime}:00Z`).toISOString();

    const bookingData = {
      coachId: coach.id,
      studentName,
      studentEmail,
      studentPhone: `${phoneCode}${studentPhone}`,
      bookingDate: iso,
      timeSlot,
      status: "requested",
    };

    setLoadingBooking(true);
    setShowConfirmation(false);

    try {
      await axios.post(`${API_BASE}/api/Booking`, bookingData);
      toast.success("Request sent! The coach will confirm soon.");
      setRequestSent(true);
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || "Request failed.");
    } finally {
      setLoadingBooking(false);
    }
  };

  const cancelRequest = () => setShowConfirmation(false);

  const resetForm = () => {
    setBookingDate("");
    setTimeSlot("");
    setStudentName("");
    setStudentEmail("");
    setStudentPhone("");
    setErrors({});
  };

  // =========================
  // Stripe helper
  // =========================
  const createGiftCheckout = async ({ coachId, amount, note }) => {
    const origin = window.location.origin;

    const successUrl =
      `${origin}/gift-success?coach=${coachId}&amount=${amount}&name=${encodeURIComponent(
        coach.fullName
      )}&note=${encodeURIComponent(note || "")}`;

    const cancelUrl = `${origin}/coach/${coachId}?gift=cancel`;

    const r = await axios.post(`${API_BASE}/api/Billing/gift/checkout`, {
      coachId: String(coachId),
      coachName: coach.fullName,
      amount,
      note: (note || "").slice(0, 120),
      successUrl,
      cancelUrl,
    });

    if (!r.data?.url) throw new Error("No checkout URL returned.");
    return r.data.url;
  };

  // =========================
  // SEO helpers
  // =========================
  const SITE_NAME = "PTfinderNow";
  const SITE_URL = "https://www.ptfindernow.com";

  const slugify = (s) =>
    String(s || "")
      .toLowerCase()
      .trim()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const specialityName =
    coach?.speciality?.name || coach?.speciality || "Speciality not set";

  const categoryName =
    (typeof coach?.category === "string" && coach.category) ||
    coach?.category?.name ||
    coach?.category ||
    "";

  const locationLabel = [coach?.city, coach?.area, coach?.country]
    .filter(Boolean)
    .join(" • ");

  const priceLabel =
    coach?.price && Number(coach.price) > 0
      ? `${coach.price} AED / session`
      : "Request price";

  const subscriptionTier =
    coach?.subscriptionTier ?? coach?.subscriptionTierId ?? 0;
  const subscriptionStatus = coach?.subscriptionStatus ?? 0;

  const isBasicTier = subscriptionTier === 1;
  const canShowGallery = !isBasicTier;
  const canShowBooking = !isBasicTier;
  const canShowReviews = true;

  const stripeAccountId = coach?.stripeAccountId;
  const stripeChargesEnabled = coach?.stripeChargesEnabled === true;
  const stripePayoutsEnabled = coach?.stripePayoutsEnabled === true;
  const stripeDetailsSubmitted = coach?.stripeDetailsSubmitted === true;

  const isStripeApproved =
    (stripePayoutsEnabled || stripeChargesEnabled) && stripeDetailsSubmitted;

  const canShowThanks =
    subscriptionTier > 0 &&
    subscriptionStatus === 1 &&
    !!stripeAccountId &&
    isStripeApproved;

  const canonicalPath = useMemo(() => {
    if (!coach?.id) return null;

    const slugParts = [
      coach.id,
      slugify(coach.fullName),
      slugify(specialityName),
      "coach",
      slugify(coach?.city || "dubai"),
      slugify(coach?.area || ""),
    ].filter(Boolean);

    return `/coach/${slugParts.join("-")}`;
  }, [coach?.id, coach?.fullName, specialityName, coach?.city, coach?.area]);

  const coachUrl = canonicalPath
    ? `${SITE_URL}${canonicalPath}`
    : `${SITE_URL}/coach/${coachId || ""}`;

  useEffect(() => {
    if (!canonicalPath) return;
    const currentPath = window.location.pathname;
    if (currentPath !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, navigate]);

  if (!coachId) {
    return <div className="p-8">Invalid coach URL.</div>;
  }

  if (!coach) {
    return (
      <div className="flex justify-center items-center h-96">
        <FaSpinner className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  const title = `${coach.fullName} — ${specialityName} in ${
    coach?.city || "Dubai"
  } | ${SITE_NAME}`;

  const metaDescription =
    (coach.description || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 155) ||
    `${coach.fullName} is a ${specialityName} in ${
      coach?.city || "Dubai"
    }. View profile, availability and request a session on ${SITE_NAME}.`;

  const ogImage = coach.profileImage || `${SITE_URL}/og-default.jpg`;

  return (
    <div className="relative mx-auto max-w-6xl px-4 py-7 sm:px-6 lg:px-8 lg:py-10">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={coachUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={coachUrl} />
        <meta property="og:image" content={ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image" content={ogImage} />
<meta property="og:image:type" content="image/jpeg" />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Person",
              name: coach.fullName,
              image: coach.profileImage || undefined,
              description: coach.description || undefined,
              jobTitle: specialityName,
              url: coachUrl,
              address: {
                "@type": "PostalAddress",
                addressLocality: coach?.city || "Dubai",
                addressRegion: coach?.area || undefined,
                addressCountry: coach?.country || "AE",
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "Service",
              name: `${specialityName} with ${coach.fullName}`,
              provider: {
                "@type": "Person",
                name: coach.fullName,
              },
              areaServed: coach?.city || "Dubai",
              url: coachUrl,
            },
          ])}
        </script>
      </Helmet>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate("/Coaches/Search")}
          className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 text-sm font-bold text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
        >
          <ArrowLeft className="h-4 w-4" /> Back to coaches
        </button>
        <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-blue-600">
          <ShieldCheck className="h-4 w-4" /> PTfinderNow professional profile
        </div>
      </div>

      <style>{`
        .react-datepicker {
          border-radius: 16px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
          font-family: inherit;
        }
        .react-datepicker__header {
          background: linear-gradient(135deg, #1d4ed8, #4338ca);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .react-datepicker__current-month,
        .react-datepicker__day-name {
          color: rgba(255,255,255,0.92);
          font-weight: 700;
        }
        .react-datepicker__day {
          border-radius: 12px;
          font-weight: 600;
        }
        .react-datepicker__day--disabled {
          color: #9ca3af !important;
          opacity: 0.55;
          cursor: not-allowed;
        }
        .react-datepicker__day--today {
          outline: 2px solid rgba(59,130,246,0.35);
          outline-offset: 1px;
        }
        .react-datepicker__day--highlighted {
          background: rgba(16,185,129,0.18) !important;
          color: #065f46 !important;
          border: 1px solid rgba(16,185,129,0.35);
        }
        .react-datepicker__day--selected,
        .react-datepicker__day--keyboard-selected {
          background: #2563eb !important;
          color: white !important;
        }
      `}</style>

      <div className="overflow-hidden rounded-[36px] border border-blue-100 bg-white shadow-[0_28px_90px_rgba(30,64,175,0.14)]">
        <div className="relative overflow-hidden border-b border-blue-100 bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-5 sm:p-7 lg:p-9">
          <div className="absolute -right-24 -top-32 h-80 w-80 rounded-full bg-blue-300/20 blur-3xl" />
          <div className="absolute -bottom-40 left-1/3 h-80 w-80 rounded-full bg-indigo-200/25 blur-3xl" />
          <div className="relative grid items-start gap-7 lg:grid-cols-[220px_minmax(0,1fr)_330px]">
            <div className="space-y-4 lg:col-span-2">
              <div className="flex flex-col gap-6 sm:flex-row">
                <div className="relative h-72 w-full flex-shrink-0 overflow-hidden rounded-[28px] border-4 border-white bg-gradient-to-br from-blue-100 to-white shadow-xl sm:h-64 sm:w-52">
                  <img
                    src={coach.profileImage}
                    className="h-full w-full object-cover object-top"
                    alt={coach.fullName}
                    draggable={false}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </div>

                <div className="min-w-0 flex-1 space-y-3 py-2">
                  <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-blue-700 shadow-sm">
                    <Sparkles className="h-3.5 w-3.5" /> Ready to help you progress
                  </div>
                  <h1 className="text-3xl font-black tracking-tight text-slate-950 sm:text-4xl lg:text-5xl">
                    {coach.fullName}
                  </h1>
                  <p className="text-base font-semibold text-blue-700 sm:text-lg">
                    {specialityName}
                    {categoryName ? ` • ${categoryName}` : ""}
                  </p>

                  <div className="flex flex-wrap gap-2.5 pt-1">
                    {locationLabel && (
                      <span className="inline-flex items-center gap-1.5 rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm sm:text-sm">
                        <MapPin className="h-4 w-4 text-blue-600" />
                        {locationLabel}
                      </span>
                    )}
                    <span className="inline-flex items-center rounded-xl bg-blue-600 px-3 py-2 text-xs font-bold text-white shadow-[0_8px_20px_rgba(37,99,235,0.24)] sm:text-sm">
                      {priceLabel}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    {coach.email && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!isLoggedIn) {
                            requireClientLogin("click_email");
                            return;
                          }
                          unlockContactAndProceed("click_email");
                        }}
                        className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-bold text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                      >
                        <Mail className="h-4 w-4" />
                        {isLoggedIn ? coach.email : "Email"}
                      </button>
                    )}

                    {coach.phoneNumber && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!isLoggedIn) {
                            requireClientLogin("click_phone");
                            return;
                          }
                          unlockContactAndProceed("click_phone");
                        }}
                        className="inline-flex items-center gap-2 rounded-xl border border-blue-100 bg-white px-3 py-2 text-xs font-bold text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
                      >
                        <Phone className="h-4 w-4" />
                        {isLoggedIn ? coach.phoneNumber : "Phone"}
                      </button>
                    )}
                  </div>

                  {isLoggedIn && clientUser && (
                    <div className="mt-2 flex items-center gap-2 text-xs text-emerald-700">
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-emerald-50 border border-emerald-100">
                        Signed in as {clientUser.fullName || clientUser.email}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          clearClientSession();
                          toast.success("Signed out.");
                        }}
                        className="underline text-gray-500 hover:text-gray-700"
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:sticky lg:top-24">
              <Card className="overflow-hidden rounded-[26px] border border-blue-100 bg-white shadow-[0_18px_45px_rgba(30,64,175,0.12)]">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600" />
                <CardContent className="space-y-4 p-5 sm:p-6">
                  <div>
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                      <CalendarDays className="h-5 w-5" />
                    </div>
                    <p className="text-lg font-black text-slate-950">
                      Start a conversation
                    </p>
                    <p className="mt-1.5 text-sm leading-6 text-slate-500">
                      Sign in securely to contact this coach and discuss your goals.
                      {canShowThanks && (
                        <>
                          {" "}You can also send a thank-you gift after training together.
                        </>
                      )}
                    </p>
                  </div>

                  <Button
                    className="h-12 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-sm font-bold text-white shadow-[0_12px_28px_rgba(37,99,235,0.28)] hover:from-blue-700 hover:to-indigo-700"
                    onClick={() => {
                      if (!isLoggedIn) {
                        requireClientLogin("click_whatsapp");
                        return;
                      }
                      unlockContactAndProceed("click_whatsapp");
                    }}
                  >
                    <FaWhatsapp />
                    Contact coach
                  </Button>

                  {canShowThanks && (
                    <Button
                      className="h-11 w-full rounded-xl border border-blue-200 bg-blue-50 text-xs font-bold text-blue-700 hover:bg-blue-100 sm:text-sm"
                      onClick={() => setShowGiftModal(true)}
                    >
                      <FaGift className="text-base" /> Send a thanks to{" "}
                      {coach.fullName}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <div className="px-5 py-7 sm:px-7 sm:py-8 lg:px-9">
          <Tabs.Root defaultValue="about">
            <Tabs.List className="mx-auto flex max-w-xl items-center justify-center gap-1 rounded-2xl border border-blue-100 bg-blue-50/80 p-1.5 text-sm">
              <Tabs.Trigger
                className="min-w-[100px] rounded-xl px-5 py-2.5 font-bold text-slate-600 transition data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_20px_rgba(37,99,235,0.22)]"
                value="about"
              >
                About
              </Tabs.Trigger>

              {canShowGallery && (
                <Tabs.Trigger
                  className="min-w-[100px] rounded-xl px-5 py-2.5 font-bold text-slate-600 transition data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_20px_rgba(37,99,235,0.22)]"
                  value="gallery"
                >
                  Gallery
                </Tabs.Trigger>
              )}

              {canShowReviews && (
                <Tabs.Trigger
                  className="min-w-[100px] rounded-xl px-5 py-2.5 font-bold text-slate-600 transition data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_20px_rgba(37,99,235,0.22)]"
                  value="reviews"
                >
                  Reviews
                </Tabs.Trigger>
              )}
            </Tabs.List>

            <Tabs.Content value="about" className="mt-8 space-y-5">
              <Card className="rounded-[26px] border border-blue-100 bg-gradient-to-br from-white to-blue-50/40 shadow-[0_14px_40px_rgba(30,64,175,0.07)]">
                <CardContent className="space-y-3 p-6 sm:p-7">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-lg">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <h2 className="text-xl font-black text-slate-950">
                    About {coach.fullName}
                  </h2>
                  <p className="whitespace-pre-line text-sm leading-7 text-slate-600 sm:text-base">
                    {coach.description ||
                      "This coach hasn't added a description yet."}
                  </p>
                </CardContent>
              </Card>

              <div className="grid gap-5 md:grid-cols-2">
                <Card className="rounded-[24px] border border-blue-100 bg-white shadow-[0_12px_35px_rgba(30,64,175,0.06)]">
                  <CardContent className="space-y-4 p-5 text-sm sm:p-6">
                    <div className="flex items-center gap-3 border-b border-blue-50 pb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                        <BadgeCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-500">Expertise</p>
                        <p className="font-bold text-slate-900">Professional focus</p>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 font-semibold uppercase">
                        Speciality
                      </div>
                      <div className="text-gray-900 mt-1">
                        {specialityName}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 font-semibold uppercase">
                        Category
                      </div>
                      <div className="text-gray-900 mt-1">{categoryName}</div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 font-semibold uppercase">
                        Location
                      </div>
                      <div className="text-gray-900 mt-1">
                        {locationLabel || "—"}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="rounded-[24px] border border-blue-100 bg-white shadow-[0_12px_35px_rgba(30,64,175,0.06)]">
                  <CardContent className="space-y-4 p-5 text-sm sm:p-6">
                    <div className="flex items-center gap-3 border-b border-blue-50 pb-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                        <ShieldCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-500">Contact</p>
                        <p className="font-bold text-slate-900">Secure client access</p>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 font-semibold uppercase">
                        Contact
                      </div>
                      <div className="mt-1 space-y-1">
                        <p>
                          <span className="font-medium">Email:</span>{" "}
                          {isLoggedIn ? coach.email || "—" : "Sign in to unlock"}
                        </p>
                        <p>
                          <span className="font-medium">Phone:</span>{" "}
                          {isLoggedIn ? coach.phoneNumber || "—" : "Sign in to unlock"}
                        </p>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-gray-500 font-semibold uppercase">
                        Price
                      </div>
                      <p className="text-gray-900 font-semibold mt-1">
                        {priceLabel}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </Tabs.Content>

            {canShowGallery && (
              <Tabs.Content value="gallery" className="mt-8">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <CoachGalleryads coachId={coach.id} />
                </div>
              </Tabs.Content>
            )}

            {canShowReviews && (
              <Tabs.Content value="reviews" className="mt-8">
                <CoachReviews coachId={coach.id} />
              </Tabs.Content>
            )}
          </Tabs.Root>
        </div>
      </div>

      {canShowBooking && (
        <div className="mt-12 sm:mt-14">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-blue-700">
                <CalendarDays className="h-4 w-4" /> Simple scheduling
              </div>
              <h3 className="text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">
                Request a session
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                Choose an available day and tell the coach how to reach you.
              </p>
            </div>
          </div>

          <Card className="overflow-hidden rounded-[32px] border border-blue-100 bg-white shadow-[0_24px_70px_rgba(30,64,175,0.12)]">
            <div className="h-1.5 bg-gradient-to-r from-blue-500 via-blue-600 to-indigo-600" />
            <CardContent className="space-y-7 p-5 sm:p-7 lg:p-8">
              <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
                <div>
                  <DatePicker
                    selected={
                      bookingDate ? new Date(`${bookingDate}T00:00:00`) : null
                    }
                    onChange={(d) => {
                      if (!d) return;
                      const ymd = ymdFromDate(d);

                      setBookingDate(ymd);
                      setTimeSlot("");
                      setErrors((prev) => ({
                        ...prev,
                        bookingDate: null,
                        timeSlot: null,
                      }));
                    }}
                    inline
                    highlightDates={highlightedDates}
                    minDate={todayLocalMidnight}
                    filterDate={(d) => {
                      const ymd = ymdFromDate(d);
                      const dd = new Date(d);
                      dd.setHours(0, 0, 0, 0);
                      if (dd < todayLocalMidnight) return false;
                      return availableDateSet.size
                        ? availableDateSet.has(ymd)
                        : true;
                    }}
                  />

                  {errors.bookingDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.bookingDate}
                    </p>
                  )}

                  {bookingDate && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between gap-2">
                        <label className="block font-semibold text-sm">
                          Time Slots for {bookingDate}
                        </label>
                        <span className="text-xs text-gray-500">
                          Tap a slot to select
                        </span>
                      </div>

                      <div className="mt-3 rounded-2xl border border-blue-300/30 bg-gradient-to-br from-blue-700 to-indigo-800 p-4 shadow-inner">
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {getTimeSlotsForDate(bookingDate).length ? (
                            getTimeSlotsForDate(bookingDate).map((slot, i) => {
                              const selected = timeSlot === slot;
                              const disabled = false;

                              return (
                                <button
                                  key={i}
                                  type="button"
                                  disabled={disabled}
                                  onClick={() => {
                                    setTimeSlot(slot);
                                    setErrors((prev) => ({
                                      ...prev,
                                      timeSlot: null,
                                    }));
                                  }}
                                  className={[
                                    "w-full rounded-xl px-3 py-2 text-sm font-semibold transition",
                                    "border",
                                    selected
                                      ? "bg-blue-600 text-white border-blue-400"
                                      : "bg-white/10 text-white border-white/20 hover:bg-white/15",
                                    disabled
                                      ? "opacity-50 cursor-not-allowed"
                                      : "",
                                  ].join(" ")}
                                >
                                  {slot}
                                </button>
                              );
                            })
                          ) : (
                            <p className="text-white/70 text-sm col-span-full">
                              No available slots for this day.
                            </p>
                          )}
                        </div>

                        {errors.timeSlot && (
                          <p className="text-rose-300 text-sm mt-2">
                            {errors.timeSlot}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4 rounded-[24px] border border-blue-100 bg-blue-50/40 p-5 sm:p-6">
                  <div className="mb-1">
                    <p className="text-lg font-black text-slate-950">Your details</p>
                    <p className="mt-1 text-sm text-slate-500">
                      We’ll send this request directly to {coach.fullName}.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <input
                        className="h-12 w-full rounded-xl border border-blue-100 bg-white px-4 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        placeholder="Your name"
                        value={studentName}
                        onChange={(e) => setStudentName(e.target.value)}
                      />
                      {errors.studentName && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.studentName}
                        </p>
                      )}
                    </div>

                    <div>
                      <input
                        className="h-12 w-full rounded-xl border border-blue-100 bg-white px-4 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        placeholder="Your email"
                        value={studentEmail}
                        onChange={(e) => setStudentEmail(e.target.value)}
                      />
                      {errors.studentEmail && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.studentEmail}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <select
                      className="h-12 rounded-xl border border-blue-100 bg-white px-3 text-sm font-semibold text-slate-700 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                    >
                      <option value="+971">+971</option>
                      <option value="+212">+212</option>
                      <option value="+20">+20</option>
                      <option value="+966">+966</option>
                    </select>

                    <div className="flex-1">
                      <input
                        className="h-12 w-full rounded-xl border border-blue-100 bg-white px-4 text-sm outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                        placeholder="Phone number"
                        value={studentPhone}
                        onChange={(e) => setStudentPhone(e.target.value)}
                      />
                      {errors.studentPhone && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.studentPhone}
                        </p>
                      )}
                    </div>
                  </div>

                  <Button
                    className="h-12 w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 font-bold text-white shadow-[0_12px_28px_rgba(37,99,235,0.28)] hover:from-blue-700 hover:to-indigo-700"
                    onClick={handleRequest}
                    disabled={loadingBooking}
                  >
                    {loadingBooking ? "Sending..." : "Request Session"}
                  </Button>

                  {requestSent && (
                    <p className="text-sm text-emerald-600">
                      Your request has been sent successfully.
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showConfirmation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md rounded-2xl shadow-2xl">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Request</h3>
              <p className="text-sm text-gray-600">
                Send session request to <b>{coach.fullName}</b>?
              </p>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={cancelRequest}>
                  Cancel
                </Button>
                <Button onClick={confirmRequest}>Confirm</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showClientLoginModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4"
          onClick={() => setShowClientLoginModal(false)}
        >
          <div
            className="relative bg-white rounded-[28px] shadow-2xl w-full max-w-md p-6 border border-gray-100 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400" />

            <div className="flex justify-between items-start mb-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-700 border border-blue-100 mb-3">
                  <FaShieldAlt />
                  Secure access
                </div>

                <h2 className="text-2xl font-bold text-gray-900">
                  Log in to contact the PT
                </h2>

              </div>

              <button
                className="text-gray-500 hover:bg-gray-100 px-3 py-1 rounded-xl"
                onClick={() => setShowClientLoginModal(false)}
              >
                ✕
              </button>
            </div>

           

            <div className="flex flex-col items-center gap-2">
              <div className={clientSigningIn ? "pointer-events-none opacity-50" : ""}>
                <GoogleSignInButton onCredential={handleGoogleCredential} />
              </div>
              {clientSigningIn && (
                <p role="status" className="flex items-center gap-2 text-sm text-gray-600">
                  <FaSpinner className="animate-spin" /> Signing you in...
                </p>
              )}
              {clientSignInError && (
                <p role="alert" className="text-sm text-red-600">{clientSignInError}</p>
              )}
            </div>

            <p className="text-xs text-center text-gray-500 mt-5 leading-5">
              By signing up I agree to the{" "}
              <Link
                to="/TermsAndConditions"
                className="font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
              >
                Terms and Conditions
              </Link>{" "}
              and{" "}
              <Link
                to="/PrivacyPolicy"
                className="font-medium text-blue-600 hover:text-blue-700 underline underline-offset-2"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      )}

      {showGiftModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4"
          onClick={() => setShowGiftModal(false)}
        >
          <Card
            className="w-full max-w-md rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <CardContent className="p-6 space-y-4">
              <h3 className="text-lg font-semibold">Send a Thanks Gift</h3>

              <textarea
                className="w-full border rounded-xl p-3 min-h-[100px]"
                placeholder="Write a short thank-you note..."
                value={giftNote}
                onChange={(e) => setGiftNote(e.target.value)}
              />

              <div className="grid grid-cols-3 gap-2">
                {[25, 50, 100].map((amount) => (
                  <Button
                    key={amount}
                    variant="outline"
                    disabled={giftLoading === amount}
                    onClick={async () => {
                      try {
                        setGiftLoading(amount);
                        const url = await createGiftCheckout({
                          coachId: coach.id,
                          amount,
                          note: giftNote,
                        });
                        window.location.href = url;
                      } catch (err) {
                        toast.error(
                          err?.response?.data?.message ||
                            err?.message ||
                            "Unable to start checkout."
                        );
                      } finally {
                        setGiftLoading(null);
                      }
                    }}
                  >
                    {giftLoading === amount ? "..." : `${amount} AED`}
                  </Button>
                ))}
              </div>

              <div className="flex justify-end">
                <Button variant="outline" onClick={() => setShowGiftModal(false)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default CoachDetail;
